/**
 * Query Cache Utility
 * 
 * Provides in-memory query deduplication to prevent duplicate API calls.
 * When multiple components request the same data simultaneously, only one
 * actual API call is made and all components receive the same result.
 */

type QueryKey = string;
type QueryFn<T> = () => Promise<T>;

interface CachedQuery<T> {
  promise: Promise<T>;
  timestamp: number;
  result?: T;
}

// In-memory cache for active queries
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const queryCache = new Map<QueryKey, CachedQuery<any>>();

// Cache TTL: 5 seconds (queries are cached for 5 seconds to prevent rapid duplicate calls)
const CACHE_TTL = 5000;

/**
 * Execute a query with deduplication.
 * If the same query is already in progress, returns the existing promise.
 * If the query result is cached and fresh, returns the cached result.
 * Otherwise, executes the query and caches the result.
 * 
 * @param key - Unique key for the query (e.g., 'getAllProjects', 'getAllSpaces:projectId')
 * @param queryFn - Function that executes the actual query
 * @param ttl - Time to live for cached results (default: 5 seconds)
 * @returns Promise that resolves to the query result
 */
export function cachedQuery<T>(
  key: QueryKey,
  queryFn: QueryFn<T>,
  ttl: number = CACHE_TTL
): Promise<T> {
  const cached = queryCache.get(key);
  const now = Date.now();

  // If query is already in progress, return the existing promise
  if (cached && cached.promise && !cached.result) {
    return cached.promise;
  }

  // If cached result is fresh, return it immediately
  if (cached && cached.result && (now - cached.timestamp) < ttl) {
    return Promise.resolve(cached.result);
  }

  // Execute new query
  const promise = queryFn().then((result) => {
    // Cache the result
    const cached = queryCache.get(key);
    if (cached) {
      cached.result = result;
      cached.timestamp = now;
    }
    return result;
  }).catch((error) => {
    // Remove from cache on error to allow retry
    queryCache.delete(key);
    throw error;
  });

  // Store the promise in cache
  queryCache.set(key, {
    promise,
    timestamp: now,
  });

  return promise;
}

/**
 * Invalidate all cache entries whose key contains the given pattern.
 * Call after mutations to prevent stale reads.
 *
 * @param pattern - Substring to match against cache keys (e.g., 'getAllFolders', 'spaceId-123')
 */
export function invalidateCache(pattern: string): void {
  for (const key of queryCache.keys()) {
    if (key.includes(pattern)) {
      queryCache.delete(key);
    }
  }
}

/**
 * Clear stale queries from cache (older than TTL).
 * Should be called periodically to prevent memory leaks.
 */
export function clearStaleQueries(): void {
  const now = Date.now();
  for (const [key, cached] of queryCache.entries()) {
    if (cached.result && (now - cached.timestamp) >= CACHE_TTL) {
      queryCache.delete(key);
    }
  }
}

// Clear stale queries every minute
if (typeof window !== 'undefined') {
  setInterval(clearStaleQueries, 60000);
}

